import { z } from "zod";
import type { Operation } from "../types";

/**
 * Market Intelligence — analysis + competitor/SWOT/finding mutations (RO-2-09,
 * ported from `excellent/src/market/actions.ts`, a `"use server"` file with no
 * route.ts equivalent). ICP CRUD is deliberately NOT here — it's already served
 * by the separate `/api/market/icp` route.ts (see `market-icp.ts`), which calls
 * the SAME `createIcpProfile`/`updateIcpProfile`/`archiveIcpProfile` db
 * functions `market/actions.ts`'s `createIcpAction`/`updateIcpAction`/
 * `archiveIcpAction` also called — those three actions have zero call sites in
 * the app (dead code, superseded by the route). Porting them here would be a
 * pure duplicate; the route.ts is L1's job when it's reached, not a new fork.
 *
 * Only `runMarketAnalysisAction`/`actFindingAction`/`dismissFindingAction` are
 * live-called (`excellent/app/market/_client.tsx`). The rest (profile propose/
 * apply, competitor CRUD, SWOT) are currently dead code in the Next tree too —
 * converted anyway for parity/completeness since they're well-defined CRUD, but
 * there is no existing call site to repoint for them.
 */

// ── Profile propose / apply ─────────────────────────────────────────────────
// `MarketProfile`/`ApplyMarketResult` (excellent/src/market/engine.ts,
// engine-actions.ts) are deep, server-only shapes; z.custom mirrors the
// existing agent-goals.ts precedent for a union/complex TS-only type.

export type MarketProfileDTO = {
  business: string;
  industry: string;
  positioningSummary: string;
  icp: unknown[];
  competitors: unknown[];
  swot: unknown[];
  leadSources: unknown[];
  source: "preset" | "llm-refined";
};

export type ApplyMarketResultDTO =
  | {
      ok: true;
      icpCreated: number;
      competitorsUpserted: number;
      swotAdded: number;
      leadSourceFindings: number;
      briefsWritten: string[];
      runId: number;
    }
  | { ok: false; error: string };

// A POST because the business description travels in the body (same reasoning
// crm.authz.ts's `apply_smart_view` uses) — genuinely read-only (no DB write),
// so it is classified `null` in crm.authz.ts.
const proposeBody = z.object({ business: z.string() });
export type MarketProposeBody = z.infer<typeof proposeBody>;
type ProposeResult = { ok: true; profile: MarketProfileDTO } | { ok: false; error: string };

// invalidation-ignore: write-nothing POST — composes a profile proposal only (RO-5-02b)
const proposeOp: Operation<void, void, MarketProposeBody, ProposeResult> = {
  method: "POST",
  path: () => "/api/market/profile/propose",
  body: proposeBody,
  response: z.custom<ProposeResult>(),
};

/** Offline-only proposal (no CLI) — used where a sync result is required. */
// invalidation-ignore: write-nothing POST — composes a profile proposal only (RO-5-02b)
const proposeOfflineOp: Operation<
  void,
  void,
  MarketProposeBody,
  { ok: true; profile: MarketProfileDTO }
> = {
  method: "POST",
  path: () => "/api/market/profile/propose-offline",
  body: proposeBody,
  response: z.custom<{ ok: true; profile: MarketProfileDTO }>(),
};

const applyBody = z.custom<MarketProfileDTO>();
const applyOp: Operation<
  void,
  void,
  MarketProfileDTO,
  ApplyMarketResultDTO | { ok: false; error: string }
> = {
  method: "POST",
  path: () => "/api/market/profile/apply",
  body: applyBody,
  response: z.custom<ApplyMarketResultDTO | { ok: false; error: string }>(),
};

// ── Live analysis ────────────────────────────────────────────────────────────

const analysisKind = z.enum(["competitors", "icp", "swot", "lead-sources", "full"]);
const runAnalysisBody = z.object({ kind: analysisKind.optional() });
export type RunAnalysisBody = z.infer<typeof runAnalysisBody>;
type RunAnalysisResult = { ok: boolean; runId?: number; error?: string };

const runAnalysisOp: Operation<void, void, RunAnalysisBody, RunAnalysisResult> = {
  method: "POST",
  path: () => "/api/market/analysis",
  body: runAnalysisBody,
  response: z.custom<RunAnalysisResult>(),
};

// ── Findings ─────────────────────────────────────────────────────────────────

const findingResult = z.object({ ok: z.boolean(), error: z.string().optional() });
type FindingResult = z.infer<typeof findingResult>;

const actFindingOp: Operation<{ id: number }, void, void, FindingResult> = {
  method: "POST",
  path: ({ id }) => `/api/market/findings/${id}/act`,
  params: z.object({ id: z.coerce.number().int().positive() }),
  response: findingResult,
};

const dismissFindingOp: Operation<{ id: number }, void, void, { ok: true }> = {
  method: "POST",
  path: ({ id }) => `/api/market/findings/${id}/dismiss`,
  params: z.object({ id: z.coerce.number().int().positive() }),
  response: z.object({ ok: z.literal(true) }),
};

// ── Competitor CRUD ──────────────────────────────────────────────────────────

const competitorInput = z.object({
  name: z.string(),
  website: z.string().nullable().optional(),
  organization_id: z.number().nullable().optional(),
  category: z.enum(["direct", "indirect", "aspirational"]).optional(),
  tier: z.enum(["primary", "secondary", "watch"]).optional(),
  positioning: z.string().nullable().optional(),
  pricing_notes: z.string().nullable().optional(),
  strengths: z.string().nullable().optional(),
  weaknesses: z.string().nullable().optional(),
  our_edge: z.string().nullable().optional(),
  threat_level: z.enum(["low", "medium", "high"]).optional(),
});
export type CompetitorInputDTO = z.infer<typeof competitorInput>;

const createCompetitorOp: Operation<void, void, CompetitorInputDTO, { ok: true; id: number }> = {
  method: "POST",
  path: () => "/api/market/competitors",
  body: competitorInput,
  response: z.object({ ok: z.literal(true), id: z.number() }),
};

const updateCompetitorOp: Operation<
  { id: number },
  void,
  Partial<CompetitorInputDTO>,
  { ok: true }
> = {
  method: "PATCH",
  path: ({ id }) => `/api/market/competitors/${id}`,
  params: z.object({ id: z.coerce.number().int().positive() }),
  body: competitorInput.partial(),
  response: z.object({ ok: z.literal(true) }),
};

const archiveCompetitorOp: Operation<{ id: number }, void, void, { ok: true }> = {
  method: "POST",
  path: ({ id }) => `/api/market/competitors/${id}/archive`,
  params: z.object({ id: z.coerce.number().int().positive() }),
  response: z.object({ ok: z.literal(true) }),
};

// ── SWOT ─────────────────────────────────────────────────────────────────────

const swotInput = z.object({
  kind: z.enum(["strength", "weakness", "opportunity", "threat"]),
  title: z.string(),
  detail: z.string().nullable().optional(),
  competitor_id: z.number().nullable().optional(),
  confidence: z.enum(["low", "medium", "high"]).optional(),
});
export type SwotInputDTO = z.infer<typeof swotInput>;

const addSwotOp: Operation<void, void, SwotInputDTO, { ok: true; id: number }> = {
  method: "POST",
  path: () => "/api/market/swot",
  body: swotInput,
  response: z.object({ ok: z.literal(true), id: z.number() }),
};

const dismissSwotOp: Operation<{ id: number }, void, void, { ok: true }> = {
  method: "POST",
  path: ({ id }) => `/api/market/swot/${id}/dismiss`,
  params: z.object({ id: z.coerce.number().int().positive() }),
  response: z.object({ ok: z.literal(true) }),
};

// ── RO-3-04 · market page READ ──────────────────────────────────────────────
// The Next `/market` page was a server component that called marketOverview /
// listIcpProfiles / listCompetitors / swotBoard / listOpenFindings /
// listRecentRuns / runningRun in-process. A client route cannot, so this op
// surfaces the same payload. ICP CRUD remains on market-icp; mutations stay
// on the ops above. Live Next page stays in-process (PRD D6).

const marketPageResponse = z.object({
  overview: z.custom<Record<string, unknown>>((v) => v != null && typeof v === "object"),
  icps: z.array(z.custom<Record<string, unknown>>((v) => v != null && typeof v === "object")),
  competitors: z.array(z.custom<Record<string, unknown>>((v) => v != null && typeof v === "object")),
  board: z.object({
    strength: z.array(z.custom<Record<string, unknown>>((v) => v != null && typeof v === "object")),
    weakness: z.array(z.custom<Record<string, unknown>>((v) => v != null && typeof v === "object")),
    opportunity: z.array(z.custom<Record<string, unknown>>((v) => v != null && typeof v === "object")),
    threat: z.array(z.custom<Record<string, unknown>>((v) => v != null && typeof v === "object")),
  }),
  findings: z.array(z.custom<Record<string, unknown>>((v) => v != null && typeof v === "object")),
  runs: z.array(z.custom<Record<string, unknown>>((v) => v != null && typeof v === "object")),
  running: z.boolean(),
});
export type MarketPageResponse = z.infer<typeof marketPageResponse>;

const pageOp: Operation<void, void, void, MarketPageResponse> = {
  method: "GET",
  path: () => "/api/market/page",
  response: marketPageResponse,
};

export const market = {
  propose: proposeOp,
  proposeOffline: proposeOfflineOp,
  apply: applyOp,
  runAnalysis: runAnalysisOp,
  actFinding: actFindingOp,
  dismissFinding: dismissFindingOp,
  createCompetitor: createCompetitorOp,
  updateCompetitor: updateCompetitorOp,
  archiveCompetitor: archiveCompetitorOp,
  addSwot: addSwotOp,
  dismissSwot: dismissSwotOp,
  page: pageOp,
};
